import { useMemo, type JSX } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import Stats from '@alltrails/core/types/Stats';
import { formatLoggedDistance } from '@alltrails/landing-pages';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Typography from '@alltrails/core/components/Typography';
import { useSelector } from '../../utils/redux';
import CommunityStat from './CommunityStat';
import styles from './styles/styles.module.scss';

const COMMUNTIY_STATS_STRINGS = defineMessages({
  CURATED_TRAILS: {
    defaultMessage: 'curated trails'
  },
  DISCOVER_UNEXPECTED_GEMS: {
    defaultMessage: 'Discover unexpected gems, even in your own backyard.'
  },
  FELLOW_EXPLORERS: {
    defaultMessage: 'fellow explorers'
  },
  SHARE_YOUR_ADVENTURES: {
    defaultMessage: 'Share your adventures and learn from our global community.'
  },
  LOGGED_MILES: {
    defaultMessage: 'logged miles'
  },
  LOGGED_KILOMETERS: { defaultMessage: 'logged kilometers' },
  NAVIGATE_YOUR_WAY: { defaultMessage: 'Navigate your way and keep a record of all your travels.' }
});

const CommunityStats = (): JSX.Element => {
  const {
    formattedDefaultMessages: {
      LOGGED_KILOMETERS,
      LOGGED_MILES,
      CURATED_TRAILS,
      DISCOVER_UNEXPECTED_GEMS,
      FELLOW_EXPLORERS,
      SHARE_YOUR_ADVENTURES,
      NAVIGATE_YOUR_WAY
    }
  } = useFormatMessage(COMMUNTIY_STATS_STRINGS);
  const languageRegionCode = useLanguageRegionCode();
  const context = useSelector(state => state.context);

  const [totalDistance, loggedDistanceMeasurement] = useMemo(
    () => [formatLoggedDistance(languageRegionCode, context.displayMetric), context.displayMetric ? LOGGED_KILOMETERS : LOGGED_MILES],
    [context, LOGGED_KILOMETERS, LOGGED_MILES, languageRegionCode]
  );

  return (
    <div className={styles.outerContainer}>
      <CommunityStat
        title={
          <Typography component="h3" variant="heading500" mb="8" className={styles.header}>
            <FormattedMessage defaultMessage="{curatedTrails}K+" values={{ curatedTrails: Stats.NumCuratedTrails }} />
          </Typography>
        }
        subtitle={
          <Typography component="h4" variant="heading300" mb="16" className={styles.subHeader}>
            {CURATED_TRAILS}
          </Typography>
        }
        text={DISCOVER_UNEXPECTED_GEMS}
      />
      <CommunityStat
        title={
          <Typography component="h3" variant="heading500" mb="8" className={styles.header}>
            <FormattedMessage defaultMessage="{fellowExplorers}M+" values={{ fellowExplorers: Stats.NumUsers }} />
          </Typography>
        }
        subtitle={
          <Typography component="h4" variant="heading300" mb="16" className={styles.subHeader}>
            {FELLOW_EXPLORERS}
          </Typography>
        }
        text={SHARE_YOUR_ADVENTURES}
      />

      <CommunityStat
        title={
          <Typography component="h3" variant="heading500" mb="8" className={styles.header}>
            <FormattedMessage defaultMessage="{totalDistance}B+" values={{ totalDistance }} />
          </Typography>
        }
        subtitle={
          <Typography component="h4" variant="heading300" mb="16" className={styles.subHeader}>
            {loggedDistanceMeasurement}
          </Typography>
        }
        text={NAVIGATE_YOUR_WAY}
      />
    </div>
  );
};

export default CommunityStats;
